import { Button, Input, Table, Alert } from '@bdo/kitchensink';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCaptureApi } from '../../hooks/useCaptureApi';
import { apiRoutes, buildApiRoute } from '../../config/apiRoutes';
import { appRoutes, buildAppRoute } from '../../config/appRoutes';
import { useLogging } from '../../hooks/useLogging';
import { approvalStatus } from '../../config/enum';
import { Dropdown, Menu } from 'antd';
import moment from 'moment';
import _ from 'lodash';

export default function List() {
    const { apiPost, apiPut,apiGet } = useCaptureApi();
    const [templates, setTemplates] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [tableLoading, setTableLoading] = useState(true);
    const { state } = useLocation();
    const [pagination, setPagination] = useState( state?.templatesPagination || { 
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        sort: null,
        search: ''
    });
    const navigate = useNavigate();   
    const logging = useLogging();
    const skipFetchRef = React.useRef(false);
    
    useEffect(() => {
        if ((pagination.search.length === 0 || pagination.search.length >= 3) && skipFetchRef.current === false) {
            fetchRecords();
        }
        skipFetchRef.current = false;
    }, [pagination.current, pagination.pageSize, pagination.search, pagination.sort]);

    
    async function fetchRecords() {
        try {
            var queryParameters = {
                page: pagination.current,
                size: pagination.pageSize,
                search: pagination.search,
                sortOrder: pagination.sort === 'ascend' ? 'asc' : 'desc',
            }
        setHasError(false);
        setTableLoading(true);        
        const response = await apiGet(buildApiRoute(apiRoutes.templates.list), queryParameters);

        if (response && response.status === 200) {
            setTemplates(response.data.records);
            setPagination((prev) => ({ 
                ...prev,
                total: response.data.totalRecords }));
        }
        else {
            logging.error("Failed to fetch templates, non success status code", { response: response });
            setHasError(true);
        }

        setTableLoading(false);
    }
    catch (e) {
        logging.error("Failed to fetch templates", { error: e });
        setTableLoading(false);
        setHasError(true);
    }        
}
    
    function handleTableChange(newpagination, sorter) {  
        const ispaginationChanged = pagination.current !== newpagination.current || pagination.pageSize !== newpagination.pageSize;
        
        if(!ispaginationChanged && sorter.order === undefined){
            skipFetchRef.current = true;
        }        
        setPagination((prev) => ({      
            ...prev,
            current: newpagination.current,
            pageSize: newpagination.pageSize,
            sort: sorter.order
        }));
    }

    const handleSearchChange = _.debounce((value) => {       
        var currentpage= pagination.current  
        
        if (value.length === 0 || value.length >= 3) {                 
        currentpage =1;
        } 

        setPagination((prev) => ({
            ...prev,
            current: currentpage,
            search: value
        }));   
    }, 500);
    
    const onRequestApproval = async (template) => {

        var payload = {
            templateId: template?.id,
            templateName: template?.name,
            templateVersion: template?.version
        }

        const response = await apiPost(buildApiRoute(apiRoutes.approval.create), payload); 
        if (response && response.status === 201) 
        {
            setTemplates( templates.map(x =>(x.id === template.id ? {...x, status: response.data.status} : x)));
        }
        else
        {
            logging.error("Failed to create the template, non success status code", { response: response });
            setHasError(true);
        }
    }

    const onCancelApproval = async (template) => 
    {
        var payload = {
            id : null,
            templateId: template?.id,            
            status: approvalStatus.Cancelled,
            version: template?.version
        }

        const response = await apiPut(buildApiRoute(apiRoutes.approval.update), payload);
        
        if (response && response.status === 200) {
            setTemplates( templates.map(x =>(x.id === template.id ? {...x, status: approvalStatus.Cancelled} : x)));
        }
        else
        {
            logging.error("Failed to cancel approval request, non success status code", { response: response });
            setHasError(true);
        }
    }
    const onDuplicate=async (template) =>{
        
        setTableLoading(true);  
        const response = await apiGet(buildApiRoute(apiRoutes.templates.duplicate,{id:template?.id}));
        if (response) {
           
            const datum=response.data;
            
            setTemplates(templates=>[...templates,datum]);

        } else {
            logging.error("Failed to duplicate the template, non success status code", { response: response });
            setHasError(true);
        }
        setTableLoading(false);  
    }
    
    const renderButton =(template) => 
    {
        switch(template?.status)
        {
            case approvalStatus.Approved:
                return <></>;
            case approvalStatus.InReview:
                return <></>;
            case approvalStatus.Pending:
                return  <>
                <Menu.Item key={`MI-2`}  onClick={ () => onCancelApproval(template) } >
                    <Button size='small' type='primary' iconName='Cancel' style={{ marginRight: 5 }} />
                    <span>Cancel approval</span>
                </Menu.Item>  
            </> 
            //If Unpublished y Declined and Cancelled
            default:                
                return <>
                    <Menu.Item key={`MI-2`}  onClick={ () => onRequestApproval(template) } >
                        <Button size='small' type='primary' iconName='DocumentApproval' style={{ marginRight: 5 }} />
                        <span>Request approval</span>
                    </Menu.Item>  
                </>
        
         }
    }

    const columns = [
        {
            title: 'Template name',
            key: 'name',
            render: (template) => (
                <Link to={buildAppRoute(appRoutes.template.view, { id: template?.id })}>
                    {template?.name}
                </Link>
            )
        },        
        {
            title: 'Version',
            key: 'version',
            dataIndex: 'version'
        },
        {
            title: 'Date created',
            key: 'createdDate',
            dataIndex: 'createdDate',
            sorter: true,
            sortOrder: pagination?.sort,
            render: (record) => (moment(record).format('DD MMM YYYY'))            
        },
        {
            title: 'Publication status',
            key: 'status',
            dataIndex: 'status'
        },
        {
            title: 'Archived',
            key: 'isArchived',
            render: (template) => (
                <>
                    { (template?.isArchived == true) ? <span>Yes</span> : <span>No</span> }
                </>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (template) => {
                const menu = (                   
                    <Menu>
                        <Menu.Item key={`MI-1`} onClick={ () => navigate(buildAppRoute(appRoutes.template.manage, { templateId: template?.id }), { state: { templatesPagination: pagination } })}>                           
                            <Button size='small' type='primary' iconName='Settings' style={{ marginRight: 5 }} />
                            <span> Manage</span>
                        </Menu.Item> 
                        <Menu.Item key={`MI-3`} onClick={()=>onDuplicate(template)}>                           
                            <Button size='small' type='primary' iconName='DuplicateRow' style={{ marginRight: 5 }} />
                            <span> Duplicate</span>
                        </Menu.Item>                     
                        {renderButton(template)}                                            
                    </Menu>
                );
                return (
                    <>
                    <Dropdown overlay={menu}>
                        <a>Actions</a>
                    </Dropdown>                   
                    </>
                )
            }
        }
    ];

    return (
        <>
            <h1>My Templates</h1>
            {hasError && <Alert type="error" message="There was an issue fetching records, please try refreshing the page." className="mb-3" />}
            <div className='controls'>
                <Input
                    className='margin-tb'
                    type='text'
                    placeholder='Search existing'
                    defaultValue={pagination?.search || ''}
                    onChange={(e) => handleSearchChange(e.target.value)}
                />
                <Button
                    type='primary'
                    onClick={() => navigate(buildAppRoute(appRoutes.template.new))}>
                    New
                </Button>
            </div>
            <Table
                columns={columns}
                loading={tableLoading}
                dataSource={templates}
                rowKey='id'                
                onChange={(pagination, filters, sorter) => handleTableChange(pagination, sorter)}
                pagination={pagination}
                
            />
        </>
    );
}
